import { Box, Flex, Input, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BaseTable, BtnPrimary, Dot } from '@/components';
import { setBreadcrumbs } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { INote, INoteFilter, INotePage, IColumn, ISelect } from '@/types';
import { showError } from '@/utils';
import { MultiValue } from 'react-select';
import { NoteFilter } from '@/pages/note/components/NoteFilter';
import { useDebouncedCallback } from 'use-debounce';
import { DEBOUNCE_TIME } from '@/constants';
import { NoteService } from '@/services/note';

function NotePage() {
  const handleChangeKeyWord = useDebouncedCallback((keyword) => {
    handleFetchData({
      ...state.query,
      page: 1,
      keyword,
    }, false, true);
  }, DEBOUNCE_TIME);

  // Breadcrumbs
  setBreadcrumbs(ROUTE_PATH.NOTE, [
    { name: trans('menu.note'), path: ROUTE_PATH.NOTE },
    { name: trans('menu.noteList') },
  ]);

  // Table columns
  const columns: IColumn<INote>[] = useMemo(() => [
    {
      key: 'name',
      title: trans('note.name'),
      render: (item: INote) => (
        <Flex align="center">
          <Text truncate maxW="200px" color="#6A6A6A" className='text-40h'>{item?.name}</Text>
        </Flex>
      ),
      width: '200px',
    },
    {
      key: 'link',
      title: trans('note.link'),
      align: 'left',
      render: (item: INote) => (
        <Flex align="center">
          <Text truncate maxW="200px" color="#718096" fontWeight={400}>{item?.link}</Text>
        </Flex>
      ),
      width: '100px',
    },
    {
      key: 'memo',
      title: trans('note.memo'),
      align: 'left',
      render: (item: INote) => (
        <Flex align="center">
          <Text truncate maxW="200px" color="#6A6A6A">{item?.memo}</Text>
        </Flex>
      ),
      width: '200px',
    },
    {
      key: 'isActive',
      title: trans('note.status'),
      align: 'left',
      render: (item: INote) => (
        <Flex align="center">
          <Flex align="center" justifyContent="center">
            <Dot red={!item?.isActive} />
            <Text color="#718096" fontWeight={400}>{item?.isActive ? '表示' : '非表示'}</Text>
          </Flex>
        </Flex>
      ),
      width: '100px',
    },
  ], []);

  // Initial query and state
  const initialQuery: INoteFilter = {
    page: 1,
    size: 15,
    orderKey: 'createdAt',
    orderBy: 'desc',
  };
  const [state, setState] = useState<INotePage>({
    data: [],
    loading: false,
    query: initialQuery,
  });
  const queryRef = useRef<INoteFilter>(initialQuery);
  const disableLoadMore = useRef(false);

  const sv = new NoteService();
  // Fetch data
  const handleFetchData = async (params: INoteFilter, append = false, reset = false) => {
    try {
      if (reset) disableLoadMore.current = false;
      if (disableLoadMore.current && !reset) return;
      setState((prev) => ({ ...prev, loading: true }));
      queryRef.current = params;
      const res = await sv.getAll(params);
      const newData = res?.notes || [];
      if (newData.length < (params.size || 15)) {
        disableLoadMore.current = true;
      }
      setState((prev) => ({
        ...prev,
        data: append ? [...prev.data, ...newData] : newData,
        query: params,
        loading: false,
      }));
    } catch (error) {
      showError(error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  // Filter handler
  const handleFilter = (arr: MultiValue<ISelect>) => {
    const filterStatus = arr
      ?.filter((item) => ['表示', '非表示'].includes(item?.label))
      ?.map((item) => item?.value);
    handleFetchData({
      ...state.query,
      page: 1,
      filterStatus,
    }, false, true);
  };

  // Initial fetch
  useEffect(() => {
    handleFetchData({
      ...state.query,
      page: 1,
    }, false, true);
  }, []);

  // Render
  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <BtnPrimary name="note.btnAdd" path={ROUTE_PATH.NOTE_CREATE} />
        <Flex justifyContent="flex-end" gap={4}></Flex>
        <NoteFilter onChange={handleFilter} />
      </Flex>
      <Box minH="50vh" maxH="95vh" width="full" bg="white" p={5} borderRadius={15} marginTop="24px">
        <Flex justifyContent="flex-end" mb="5px">
          <Input
            onChange={(e) => handleChangeKeyWord(e.target.value)}
            width="200px"
            placeholder={'Search...'}
          />
        </Flex>
        <BaseTable<INote>
          rowKey="id"
          className="tbl-note"
          columns={columns}
          loading={state.loading}
          data={state.data}
          scroll
          maxH="calc(100vh - 300px)"
          minH="calc(100vh - 300px)"
          editPath={ROUTE_PATH.NOTE}
          loadMore={() => {
            if (disableLoadMore.current) return;
            const nextPage = (queryRef.current?.page || 1) + 1;
            handleFetchData({ ...queryRef.current, page: nextPage }, true, false);
          }}
          onSort={(orderKey: string, orderBy: 'asc' | 'desc') => {
            handleFetchData({
              ...state.query,
              page: 1,
              orderKey,
              orderBy,
            }, false, true);
          }}
        />
      </Box>
    </>
  );
}

export default NotePage;
